/**
 * AiNotesPanel — optional guidance for the AI plus the "generate" action.
 * Regenerating over an existing (possibly edited) summary asks for an inline
 * confirmation first.
 */

import { useEffect, useRef, useState } from "react";
import { Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import LoaderLoop from "../../shared/loader/LoaderLoop";

export const MAX_AI_NOTES = 2000;

function AiNotesPanel({
  aiNotes,
  setAiNotes,
  summaryOk,
  summaryMeta,
  selectedCount,
  overLimit,
  isGenerating,
  generateError,
  onGenerate,
}) {
  const [confirmReplace, setConfirmReplace] = useState(false);
  const textareaRef = useRef(null);

  // Auto-grow the textarea with its content.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 260)}px`;
  }, [aiNotes]);

  const canGenerate = selectedCount > 0 && !overLimit && !isGenerating;

  const handleClick = () => {
    if (!canGenerate) return;
    if (summaryOk && !confirmReplace) {
      setConfirmReplace(true);
      return;
    }
    setConfirmReplace(false);
    onGenerate();
  };

  return (
    <div className="ais-notes-panel">
      <p className="ais-hint">
        מה חשוב שהסיכום יכלול? למי הוא מיועד (הורים, מסגרת חינוכית, רופא מפנה)? איזה טון
        ואורך? (לא חובה)
      </p>

      <div className="ais-notes-wrap">
        <textarea
          ref={textareaRef}
          className="ais-notes"
          value={aiNotes}
          onChange={(e) => setAiNotes(e.target.value.slice(0, MAX_AI_NOTES))}
          placeholder="לדוגמה: סיכום להורים לקראת ועדת השמה, להדגיש התקדמות בשפה ולהוסיף המלצות לבית"
          rows={3}
          maxLength={MAX_AI_NOTES}
          disabled={isGenerating}
          aria-label="הנחיות ל-AI"
        />
        <span className="ais-notes-counter">
          {aiNotes.length}/{MAX_AI_NOTES}
        </span>
      </div>

      {generateError && <div className="prw-field-error">{generateError}</div>}

      {summaryMeta?.truncated > 0 && !isGenerating && (
        <p className="ais-soft-note">חלק מהטיפולים קוצרו בשל אורכם.</p>
      )}
      {summaryMeta?.dropped > 0 && !isGenerating && (
        <p className="ais-soft-note">
          {summaryMeta.dropped} טיפולים לא נכללו בסיכום בשל מגבלת אורך — צמצמו את הבחירה
          לתוצאה מלאה.
        </p>
      )}

      {confirmReplace ? (
        <div className="ais-confirm">
          <span>הסיכום הנוכחי (כולל עריכות) יוחלף בסיכום חדש.</span>
          <div className="ais-confirm-actions">
            <button type="button" className="prw-btn ghost" onClick={() => setConfirmReplace(false)}>
              ביטול
            </button>
            <button type="button" className="prw-btn primary" onClick={handleClick}>
              <ArrowsClockwise size={16} weight="bold" />
              החלפה וצור מחדש
            </button>
          </div>
        </div>
      ) : (
        <div className="ais-generate-row">
          <button
            type="button"
            className="prw-btn primary ais-generate-btn"
            onClick={handleClick}
            disabled={!canGenerate}
          >
            {isGenerating ? (
              <LoaderLoop size="xs" />
            ) : summaryOk ? (
              <ArrowsClockwise size={16} weight="bold" />
            ) : (
              <Sparkle size={16} weight="fill" />
            )}
            {isGenerating ? "מייצר סיכום…" : summaryOk ? "צור סיכום מחדש" : "צור סיכום עם AI"}
          </button>
          {selectedCount === 0 && !isGenerating && (
            <span className="ais-generate-hint">בחרו לפחות טיפול אחד</span>
          )}
          {isGenerating && (
            <span className="ais-generate-hint">
              מנתח {selectedCount} טיפולים — זה עשוי לקחת עד דקה
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default AiNotesPanel;
